import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SupportFilePicker } from "@/components/SupportFilePicker";
import { SupportResponseTimeNote } from "@/components/SupportResponseTimeNote";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export const SupportTicketComposer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [authorName, setAuthorName] = useState(() => user?.name || localStorage.getItem(`support-name-${user?.id}`) || "");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const form = new FormData();
      form.append("subject", subject.trim()); form.append("body", body.trim());
      form.append("author_name", authorName.trim());
      files.forEach(file => form.append("files", file));
      const { data } = await api.post("/support/tickets/with-attachments", form);
      if (authorName.trim()) localStorage.setItem(`support-name-${user?.id}`, authorName.trim());
      navigate(`/assistenza/${data.id}`, { state: { ticketCreated: true } });
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Impossibile aprire il ticket. Riprova.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="support-new-ticket">
      <button type="button" onClick={() => navigate("/assistenza")} className="text-sm text-[#707070] inline-flex items-center gap-2 mb-10 hover:text-[#1A1A1A]"
              data-testid="support-new-back"><ArrowLeft className="w-4 h-4" />Tutti i ticket</button>
      <h1 className="font-serif text-4xl sm:text-5xl mb-5 text-[#1A1A1A]" data-testid="support-new-title">Nuovo ticket</h1>
      <div className="mb-9"><SupportResponseTimeNote /></div>
      <form onSubmit={submit} className="space-y-7" data-testid="support-new-form">
        <div>
          <label htmlFor="support-author-name" className="block text-sm font-medium mb-2">Il tuo nome</label>
          <Input id="support-author-name" value={authorName} onChange={event => setAuthorName(event.target.value)}
                 maxLength={120} placeholder="Nome e cognome (facoltativo)"
                 className="rounded-sm bg-white h-12" data-testid="support-new-author-name" />
        </div>
        <div>
          <label htmlFor="support-subject" className="block text-sm font-medium mb-2">Oggetto</label>
          <Input id="support-subject" value={subject} onChange={event => setSubject(event.target.value)}
                 required minLength={3} maxLength={160} placeholder="Oggetto della richiesta"
                 className="rounded-sm bg-white h-12" data-testid="support-new-subject" />
        </div>
        <div>
          <label htmlFor="support-body" className="block text-sm font-medium mb-2">Messaggio</label>
          <Textarea id="support-body" value={body} onChange={event => setBody(event.target.value)}
                    required maxLength={10000} rows={9} placeholder="Scrivi qui il tuo messaggio..."
                    className="rounded-sm bg-white resize-y" data-testid="support-new-body" />
        </div>
        <SupportFilePicker files={files} onChange={setFiles} disabled={busy} idPrefix="support-new" />
        {error && <p className="text-sm text-[#8C3A3A]" role="alert" data-testid="support-new-error">{error}</p>}
        <Button type="submit" disabled={busy || subject.trim().length < 3 || !body.trim()}
                className="rounded-sm bg-[#1A1A1A] text-white hover:bg-[#424242] gap-2"
                data-testid="support-new-submit"><Send className="w-4 h-4" />{busy ? "Invio..." : "Apri ticket"}</Button>
      </form>
    </div>
  );
};